import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Summary } from '../types';
import { formatTimestampToDay } from 'support/helpers/dateTime/dateTime';

type PriceCatalogSummaryValues = {
  lineCount: number;
  creationDate?: number;
  catalogIdentifier?: string;
};

const usePriceCatalogSummaryValues = (): PriceCatalogSummaryValues => {
  const [lineItems, catalogDate, catalogIdentifier] = useWatch({
    name: ['line_items', 'info.0.catalog_date', 'info.0.catalog_identifier'],
  });

  return useMemo(
    () => ({
      catalogIdentifier,
      lineCount: lineItems?.length ?? 0,
      creationDate: catalogDate,
    }),
    [catalogIdentifier, lineItems?.length, catalogDate],
  );
};

export const usePriceCatalogSummary = (): Summary => {
  const { t } = useTranslation();
  const values = usePriceCatalogSummaryValues();

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.priceCatalog.catalogIdentifier.label'),
            value: values.catalogIdentifier ?? '—',
            test: (mode: 'create-document' | 'send-confirmation-modal' | 'view-document') => {
              if (mode === 'send-confirmation-modal') {
                return true;
              }
              return false;
            },
          },
          {
            label: t('webedi:documentSummary.priceCatalog.lineCount.label'),
            value: Math.round(values.lineCount),
            test: (mode: 'create-document' | 'send-confirmation-modal' | 'view-document') => {
              if (mode !== 'send-confirmation-modal') {
                return true;
              }
              return false;
            },
          },
          {
            label: t('webedi:documentSummary.priceCatalog.creationDate.label'),
            value: formatTimestampToDay(values.creationDate) ?? '—',
          },
        ],
      ],
    };
  }, [t, values]);
};
